import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { createPortal } from "react-dom";
import {
  BOARD_LIMITS,
  LINE_H,
  LINES_PER_PAGE,
  nextLine,
  PAGE_H,
  PAGE_MARGIN,
  PAGE_TOP,
  PAGE_W,
  strokeLine,
  type BoardMark,
  type BoardStamp,
  type BoardStroke,
} from "../../../shared/kitchen/board";
import type { KitchenList, ListItem } from "../../../shared/kitchen/models";
import { itemKey } from "../../../shared/kitchen/validate";
import { strokesHit, WHITEBOARD_COLOR_NAMES, WHITEBOARD_COLORS, WHITEBOARD_LIMITS, type WhiteboardColor } from "../../../shared/whiteboard";
import { useAppUi } from "../../app/uiContext";
import { readPref, writePref } from "../../data/persist";
import { Icon } from "../../ui/Icon";
import { drawStroke } from "../../whiteboard/ink";
import { isTempId, tempId, useListActions, useLists } from "./api";
import { FoodGlyph, foodPath } from "./glyphs";
import { AISLES, shortcutFor } from "./shortcuts";
import { useListBoard } from "./useListBoard";
import "../../whiteboard/whiteboard.css";

type Tool = "pen" | "eraser";
interface Prefs {
  tool: Tool;
  color: WhiteboardColor;
}
type UndoStep = { kind: "add"; id: string; addedText: string | null } | { kind: "erase"; marks: BoardMark[] };

const PEN_COLORS: WhiteboardColor[] = ["graphite", "lake", "ember", "pine", "plum"];
const PEN_SIZE = 6;
const ERASER_R = 18;
const STAMP_TEXT_X = PAGE_MARGIN + 120;
const newId = () => crypto.randomUUID().replace(/-/g, "").slice(0, 20);
const rowTop = (line: number) => PAGE_TOP + line * LINE_H;

/** Where a stamp sits, for the eraser (page units). */
function stampHit(s: BoardStamp, x: number, y: number): boolean {
  const top = rowTop(s.line);
  const right = STAMP_TEXT_X + s.text.length * 30 + 20;
  return x >= PAGE_MARGIN - ERASER_R && x <= right + ERASER_R && y >= top + 8 - ERASER_R && y <= top + LINE_H + ERASER_R;
}

/**
 * The shopping board: a full-screen lined notepad for writing the shopping list by
 * hand, one item per line, beside a compact palette of shortcuts, one aisle at a
 * time. Tapping a shortcut writes it on the next line and puts it on the list. The
 * board is the list's own (not the daily whiteboard) and stays until it's cleared.
 *
 * It covers the app as a solid screen of its own, not a sheet: a see-through,
 * blurred backdrop over the live screen behind made every pen stroke repaint the
 * whole wall, which is what made writing lag.
 */
export function ShoppingBoardScreen({ list, onClose }: { list: KitchenList; onClose: () => void }) {
  const q = useLists();
  const actions = useListActions();
  const { toast } = useAppUi();
  const board = useListBoard(list.id);
  const [page, setPage] = useState(0);
  const [tab, setTab] = useState<"shortcuts" | "list">("shortcuts");
  const [aisle, setAisle] = useState<string | null>(null);

  // ---- the screen: hide the app behind it, Escape closes, focus comes back ----
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const root = document.documentElement;
    root.classList.add("sb-open");
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      // A sheet opened on top (none today) gets Escape first.
      if (e.key === "Escape" && !document.querySelector(".sheet")) closeRef.current();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      root.classList.remove("sb-open");
      window.removeEventListener("keydown", onKey);
      opener?.focus?.();
    };
  }, []);

  const [prefs, setPrefsState] = useState<Prefs>(() => ({ tool: "pen", color: "graphite", ...readPref<Partial<Prefs>>("shoppingBoard", {}) }));
  const setPrefs = (p: Partial<Prefs>) =>
    setPrefsState((cur) => {
      const next = { ...cur, ...p };
      writePref("shoppingBoard", next);
      return next;
    });

  // ---- the list beside the board ----
  const items = useMemo(() => (q.data?.items ?? []).filter((i) => i.listId === list.id), [q.data, list.id]);
  const open = items.filter((i) => !i.checkedAt);
  const openKeys = useMemo(() => new Set(open.map((i) => itemKey(i.text))), [open]);
  const recent = useMemo(
    () =>
      (q.data?.suggestions ?? [])
        .filter((s) => s.listId === list.id && !openKeys.has(itemKey(s.text)))
        .sort((a, b) => b.lastUsedAt.localeCompare(a.lastUsedAt))
        .slice(0, 12),
    [q.data, list.id, openKeys],
  );

  // ---- pages: open on the last page written on ----
  const all = useMemo(() => [...board.marks.values()], [board.marks]);
  const lastPage = all.reduce((p, m) => Math.max(p, m.page), 0);
  const opened = useRef(false);
  useEffect(() => {
    if (!board.loaded || opened.current) return;
    opened.current = true;
    setPage(lastPage);
  }, [board.loaded, lastPage]);
  const onPage = useMemo(() => all.filter((m) => m.page === page).sort((a, b) => a.at - b.at || (a.id < b.id ? -1 : 1)), [all, page]);
  const strokes = useMemo(() => onPage.filter((m): m is BoardStroke => m.kind === "stroke"), [onPage]);
  const stamps = useMemo(() => onPage.filter((m): m is BoardStamp => m.kind === "stamp"), [onPage]);
  const pages = Math.min(BOARD_LIMITS.pages, Math.max(lastPage, page) + 1);
  const canTurnOn = page < BOARD_LIMITS.pages - 1 && (page < lastPage || onPage.length > 0);

  // ---- layout: the page, as large as fits ----
  const wrapRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      const scale = Math.min(r.width / PAGE_W, r.height / PAGE_H);
      setBox({ w: Math.floor(PAGE_W * scale), h: Math.floor(PAGE_H * scale) });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const scale = box.w / PAGE_W;

  // ---- drawing the committed ink ----
  const inkRef = useRef<HTMLCanvasElement>(null);
  const liveRef = useRef<HTMLCanvasElement>(null);
  const drawn = useRef(new Set<string>());
  const prep = useCallback(
    (c: HTMLCanvasElement | null, clear: boolean) => {
      if (!c || !box.w) return null;
      const dpr = window.devicePixelRatio || 1;
      const w = Math.round(box.w * dpr);
      const h = Math.round(box.h * dpr);
      if (c.width !== w || c.height !== h) {
        c.width = w;
        c.height = h;
        if (c === inkRef.current) drawn.current = new Set();
      }
      const ctx = c.getContext("2d")!;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      if (clear) ctx.clearRect(0, 0, w, h);
      ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);
      return ctx;
    },
    [box.w, box.h, scale],
  );
  useLayoutEffect(() => {
    drawn.current = new Set();
  }, [board.epoch, box.w, page]);
  useLayoutEffect(() => {
    const ctx = prep(inkRef.current, drawn.current.size === 0);
    if (!ctx) return;
    for (const s of strokes) {
      if (drawn.current.has(s.id)) continue;
      drawStroke(ctx, s);
      drawn.current.add(s.id);
    }
  }, [strokes, board.epoch, prep, page]);

  // ---- undo ----
  const undo = useRef<UndoStep[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const pushUndo = (s: UndoStep) => {
    undo.current = [...undo.current.slice(-99), s];
    setCanUndo(true);
  };

  // ---- shortcuts: write it on the next line, and put it on the list ----
  const [flash, setFlash] = useState<string | null>(null);
  useEffect(() => {
    if (!flash) return;
    const t = window.setTimeout(() => setFlash(null), 1400);
    return () => clearTimeout(t);
  }, [flash]);

  const addToList = (text: string) =>
    actions.add.mutate({ listId: list.id, text, quantity: null, tempId: tempId("item") }, { onError: (e) => toast(`Couldn’t add “${text}”: ${e.message}`) });

  const stamp = (text: string, glyph: string) => {
    const key = itemKey(text);
    const onList = openKeys.has(key);
    const stamped = all.some((m) => m.kind === "stamp" && itemKey(m.text) === key);
    setFlash(key);
    if (onList && stamped) return toast(`${text} is already on the list`);
    if (!onList) addToList(text);
    if (stamped) return;
    const spot = nextLine(all, page);
    if (!spot) return toast("The board is full. Clear it to start a fresh list.");
    const mark: BoardStamp = { kind: "stamp", id: newId(), page: spot.page, line: spot.line, glyph, text, at: Date.now() };
    board.add(mark);
    pushUndo({ kind: "add", id: mark.id, addedText: onList ? null : text });
    if (spot.page !== page) setPage(spot.page);
  };

  const doUndo = useCallback(() => {
    const step = undo.current.pop();
    setCanUndo(undo.current.length > 0);
    if (!step) return;
    if (step.kind === "erase") return board.restore(step.marks);
    const mark = board.marks.get(step.id);
    board.erase([step.id]);
    if (mark) setPage(mark.page);
    // Undoing a shortcut takes back the item it put on the list, too.
    if (step.addedText) {
      const item = (q.data?.items ?? []).find((i) => i.listId === list.id && !i.checkedAt && itemKey(i.text) === itemKey(step.addedText!));
      if (item && !isTempId(item.id)) actions.remove.mutate(item);
    }
  }, [board, q.data, list.id, actions.remove]);

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
        e.preventDefault();
        doUndo();
      }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [doUndo]);

  // ---- pen and eraser ----
  const active = useRef<{ pointer: number; tool: Tool; pts: number[]; last: number; at: number; erased: BoardMark[]; path: number[]; rect: DOMRect } | null>(null);

  // The page's place on screen is read once per stroke, not for every pen sample.
  const toPage = (e: { clientX: number; clientY: number }, r: DOMRect = active.current?.rect ?? liveRef.current!.getBoundingClientRect()) => {
    return [Math.round(((e.clientX - r.left) / r.width) * PAGE_W), Math.round(((e.clientY - r.top) / r.height) * PAGE_H)] as const;
  };

  const paintLive = (cursor?: readonly [number, number]) => {
    const ctx = prep(liveRef.current, true);
    const a = active.current;
    if (!ctx || !a) return;
    if (a.tool === "pen") drawStroke(ctx, { id: "live", tool: "ink", color: prefs.color, size: PEN_SIZE, at: a.at, pts: a.pts });
    else if (cursor) {
      ctx.beginPath();
      ctx.arc(cursor[0], cursor[1], ERASER_R, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(40, 36, 32, 0.45)";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 4]);
      ctx.stroke();
    }
  };

  const eraseAt = (x: number, y: number) => {
    const a = active.current!;
    // Fill in the path between samples, so a quick swipe can't skip over a line.
    const probe: number[] = [];
    const px = a.path.length ? a.path[a.path.length - 2]! : x;
    const py = a.path.length ? a.path[a.path.length - 1]! : y;
    const steps = Math.max(1, Math.ceil(Math.hypot(x - px, y - py) / (ERASER_R / 2)));
    for (let i = 1; i <= steps; i++) probe.push(px + ((x - px) * i) / steps, py + ((y - py) * i) / steps);
    a.path.push(x, y);
    const hit = new Set(strokesHit(strokes, probe, ERASER_R));
    for (const s of stamps) {
      for (let i = 0; i < probe.length; i += 2) if (stampHit(s, probe[i]!, probe[i + 1]!)) hit.add(s.id);
    }
    const fresh = [...hit].filter((id) => !a.erased.some((m) => m.id === id));
    if (fresh.length) {
      a.erased.push(...fresh.map((id) => board.marks.get(id)!).filter(Boolean));
      board.erase(fresh);
    }
  };

  const onDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!board.loaded || active.current || (e.pointerType === "mouse" && e.button !== 0)) return;
    e.preventDefault();
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* not a live pointer (already lifted): nothing to capture */
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const [x, y] = toPage(e, rect);
    active.current = { pointer: e.pointerId, tool: prefs.tool, pts: [x, y, 0], last: performance.now(), at: Date.now(), erased: [], path: [], rect };
    if (prefs.tool === "eraser") eraseAt(x, y);
    paintLive([x, y]);
  };

  const onMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const a = active.current;
    if (!a || e.pointerId !== a.pointer) return;
    // Use every sample the device reported (pens and fast hands send several per frame).
    const events = typeof e.nativeEvent.getCoalescedEvents === "function" ? e.nativeEvent.getCoalescedEvents() : [e.nativeEvent];
    let last: readonly [number, number] = [a.pts[a.pts.length - 3]!, a.pts[a.pts.length - 2]!];
    for (const ev of events.length ? events : [e.nativeEvent]) {
      const [x, y] = toPage(ev);
      if (a.tool === "eraser") {
        eraseAt(x, y);
        last = [x, y];
        continue;
      }
      const t = performance.now();
      if (Math.hypot(x - last[0], y - last[1]) < 1.5 || a.pts.length >= WHITEBOARD_LIMITS.pointsPerStroke * 3) continue;
      a.pts.push(x, y, Math.max(1, Math.round(t - a.last)));
      a.last = t;
      last = [x, y];
    }
    paintLive(last);
  };

  const finish = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const a = active.current;
    if (!a || e.pointerId !== a.pointer) return;
    active.current = null;
    prep(liveRef.current, true);
    if (a.tool === "eraser") {
      if (a.erased.length) pushUndo({ kind: "erase", marks: a.erased });
      return;
    }
    const pts = a.pts.length === 3 ? [...a.pts, a.pts[0]! + 1, a.pts[1]!, 16] : a.pts; // a tap leaves a dot
    const mark: BoardStroke = { kind: "stroke", id: newId(), page, line: strokeLine(pts), tool: "ink", color: prefs.color, size: PEN_SIZE, at: a.at, pts };
    board.add(mark);
    pushUndo({ kind: "add", id: mark.id, addedText: null });
  };

  const [confirmClear, setConfirmClear] = useState(false);
  useEffect(() => {
    if (!confirmClear) return;
    const t = window.setTimeout(() => setConfirmClear(false), 3500);
    return () => clearTimeout(t);
  }, [confirmClear]);
  /** Clears every page (the list itself is untouched); undo brings it all back. */
  const clear = () => {
    if (!confirmClear) return setConfirmClear(true);
    setConfirmClear(false);
    board.erase(all.map((m) => m.id));
    pushUndo({ kind: "erase", marks: all });
    setPage(0);
  };

  // ---- the palette: one aisle at a time (Recent first, when there is any) ----
  const shown = aisle ?? (recent.length ? "recent" : AISLES[0]!.id);
  const current = AISLES.find((a) => a.id === shown);
  const tiles =
    shown === "recent" ? recent.map((r) => ({ name: r.text, glyph: shortcutFor(r.text)?.glyph ?? "bag" })) : (current?.items ?? []).map((s) => ({ name: s.name, glyph: s.glyph }));

  const tile = (text: string, glyph: string) => {
    const k = itemKey(text);
    const on = openKeys.has(k);
    return (
      <button key={text} className={`sb-tile ${on ? "on" : ""} ${flash === k ? "flash" : ""}`} onClick={() => stamp(text, glyph)} aria-label={on ? `${text}, on the list` : `Add ${text}`}>
        <FoodGlyph name={glyph} size="1.6rem" />
        <span>{text}</span>
        {on && (
          <span className="sb-tile-on" aria-hidden="true">
            <Icon name="check" size="0.65rem" />
          </span>
        )}
      </button>
    );
  };

  const empty = board.loaded && all.length === 0;

  return createPortal(
    <div className={`sb-screen show-${tab}`} role="dialog" aria-modal="true" aria-label={list.name} tabIndex={-1} ref={panelRef}>
      <header className="sb-bar">
        <h2>{list.name}</h2>
        <div className="sb-dock" role="toolbar" aria-label="Board tools">
          <div className="wb-group">
            <button className={`wb-tool ${prefs.tool === "pen" ? "on" : ""}`} onClick={() => setPrefs({ tool: "pen" })} aria-pressed={prefs.tool === "pen"} aria-label="Pen" title="Pen">
              <Icon name="pen" size="1.35rem" />
            </button>
            <button className={`wb-tool ${prefs.tool === "eraser" ? "on" : ""}`} onClick={() => setPrefs({ tool: "eraser" })} aria-pressed={prefs.tool === "eraser"} aria-label="Eraser" title="Eraser">
              <Icon name="eraser" size="1.35rem" />
            </button>
          </div>
          <div className="wb-group wb-colors">
            {PEN_COLORS.map((c) => (
              <button
                key={c}
                className={`wb-swatch ${prefs.color === c && prefs.tool === "pen" ? "on" : ""}`}
                style={{ ["--ink" as string]: WHITEBOARD_COLORS[c] }}
                onClick={() => setPrefs({ color: c, tool: "pen" })}
                aria-pressed={prefs.color === c}
                aria-label={WHITEBOARD_COLOR_NAMES[c]}
                title={WHITEBOARD_COLOR_NAMES[c]}
              />
            ))}
          </div>
          <div className="wb-group">
            <button className="wb-tool" onClick={doUndo} disabled={!canUndo} aria-label="Undo" title="Undo">
              <Icon name="undo" size="1.3rem" />
            </button>
            <button className={`wb-tool wb-clear ${confirmClear ? "confirm" : ""}`} onClick={clear} disabled={!all.length} aria-label={confirmClear ? "Tap again to clear the board" : "Clear the board"}>
              <Icon name="trash" size="1.25rem" />
              {confirmClear && <span>Tap again</span>}
            </button>
          </div>
        </div>
        <div className="sb-pages">
          <button className="icon-btn" onClick={() => setPage((p) => p - 1)} disabled={page === 0} aria-label="Previous page">
            <Icon name="chevronLeft" />
          </button>
          <span>
            Page {page + 1} of {pages}
          </span>
          <button className="icon-btn" onClick={() => setPage((p) => p + 1)} disabled={!canTurnOn} aria-label={page < lastPage ? "Next page" : "New page"}>
            <Icon name={page < lastPage ? "chevronRight" : "plus"} />
          </button>
        </div>
        <button className="icon-btn sb-close" onClick={onClose} aria-label="Close the shopping board">
          <Icon name="close" />
        </button>
      </header>

      <div className="sb">
        <div className="segmented sb-tabs" role="tablist" aria-label="Beside the board">
          <button role="tab" aria-selected={tab === "shortcuts"} className={tab === "shortcuts" ? "on" : ""} onClick={() => setTab("shortcuts")}>
            Shortcuts
          </button>
          <button role="tab" aria-selected={tab === "list"} className={tab === "list" ? "on" : ""} onClick={() => setTab("list")}>
            On the list{open.length ? ` (${open.length})` : ""}
          </button>
        </div>

        <section className="sb-palette" aria-label="Shortcuts">
          <nav className="sb-aisles" aria-label="Aisles">
            {recent.length > 0 && (
              <button className={`sb-aisle ${shown === "recent" ? "on" : ""}`} onClick={() => setAisle("recent")} aria-pressed={shown === "recent"}>
                <Icon name="clock" size="1.25rem" />
                <span>Recent</span>
              </button>
            )}
            {AISLES.map((a) => (
              <button key={a.id} className={`sb-aisle ${shown === a.id ? "on" : ""}`} onClick={() => setAisle(a.id)} aria-pressed={shown === a.id}>
                <FoodGlyph name={a.glyph} size="1.25rem" />
                <span>{a.short ?? a.name}</span>
              </button>
            ))}
          </nav>
          <div className="sb-shelf">
            <h3>{shown === "recent" ? "Recently bought" : current?.name}</h3>
            <div className="sb-tiles">{tiles.map((t) => tile(t.name, t.glyph))}</div>
          </div>
        </section>

        <section className="sb-pad" aria-label="Shopping board">
          <div className="sb-wrap" ref={wrapRef} style={{ ["--page-ratio" as string]: `${PAGE_W} / ${PAGE_H}` }}>
            <div className="sb-page" style={{ width: box.w, height: box.h }}>
              <svg className="sb-paper" viewBox={`0 0 ${PAGE_W} ${PAGE_H}`} aria-hidden="true">
                <text className="sb-heading" x={PAGE_MARGIN + 20} y={PAGE_TOP - 34}>
                  {list.name}
                </text>
                {pages > 1 && (
                  <text className="sb-pageno" x={PAGE_W - 40} y={PAGE_TOP - 34} textAnchor="end">
                    {page + 1}
                  </text>
                )}
                <line className="sb-margin" x1={PAGE_MARGIN} y1={0} x2={PAGE_MARGIN} y2={PAGE_H} />
                {Array.from({ length: LINES_PER_PAGE }, (_, i) => (
                  <line key={i} className="sb-rule" x1={0} y1={rowTop(i + 1)} x2={PAGE_W} y2={rowTop(i + 1)} />
                ))}
                {stamps.map((s) => (
                  <g key={s.id} className="sb-stamp">
                    <svg x={PAGE_MARGIN + 28} y={rowTop(s.line) + 20} width={70} height={70} viewBox="0 0 24 24">
                      <path d={foodPath(s.glyph)} />
                    </svg>
                    <text x={STAMP_TEXT_X} y={rowTop(s.line) + 74}>
                      {s.text}
                    </text>
                  </g>
                ))}
              </svg>
              <canvas className="sb-ink" ref={inkRef} />
              <canvas
                className={`sb-live tool-${prefs.tool}`}
                ref={liveRef}
                onPointerDown={onDown}
                onPointerMove={onMove}
                onPointerUp={finish}
                onPointerCancel={finish}
                aria-label="Shopping board. Write one item on each line with a finger, pen or mouse."
                role="img"
              />
              {empty && <p className="sb-empty">Write one thing on each line, or tap a shortcut to add it.</p>}
              {board.full && <p className="sb-notice">The board is full. Clear it to start a fresh list.</p>}
              {board.error && !board.loaded && <p className="sb-notice">Couldn’t load the board. Trying again…</p>}
            </div>
          </div>
        </section>

        <aside className="sb-list" aria-label={`On the ${list.name}`}>
          <h3>
            On the list <span className="sb-count">{open.length}</span>
          </h3>
          {open.length === 0 ? (
            <p className="muted sb-none">Nothing yet. Tap a shortcut to add it.</p>
          ) : (
            <ul>
              {open.map((i) => (
                <ListRow key={i.id} item={i} onToggle={() => actions.toggle.mutate(i, { onError: (e) => toast(`Couldn’t update “${i.text}”: ${e.message}`) })} />
              ))}
            </ul>
          )}
          {items.length > open.length && <p className="muted sb-bought">{items.length - open.length} checked off</p>}
        </aside>
      </div>
    </div>,
    document.body,
  );
}

function ListRow({ item, onToggle }: { item: ListItem; onToggle: () => void }) {
  const pending = isTempId(item.id);
  return (
    <li className={`sb-row ${pending ? "pending" : ""}`}>
      <button className="task-check" onClick={onToggle} aria-label={`Check off ${item.text}`} disabled={pending}>
        <span className="box" />
      </button>
      <FoodGlyph name={shortcutFor(item.text)?.glyph ?? "bag"} size="1.35rem" className="sb-row-glyph" />
      <span className="sb-row-text">{item.text}</span>
      {item.quantity && <span className="kl-qty-pill">{item.quantity}</span>}
    </li>
  );
}
